import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, AdminLayout } from "./AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2, Edit, GripVertical, Home, Briefcase } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_OPTIONS = [
  "Globe", "Database", "Palette", "Shield", "Zap", "Search",
  "ShoppingCart", "Code", "Server", "Monitor", "Smartphone", "Settings",
  "Mail", "FileText", "Layout", "Layers", "BarChart", "Cloud",
];

export const AdminServiceBoxes = () => {
  const { token } = useAuth();
  const [boxes, setBoxes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBox, setCurrentBox] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    icon: "Globe",
    show_on_homepage: true,
    show_on_services: true,
  });

  const fetchBoxes = async () => {
    try {
      const res = await axios.get(`${API}/service-boxes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBoxes(res.data);
    } catch (error) {
      console.error("Error fetching service boxes:", error);
    }
  };

  useEffect(() => { fetchBoxes(); }, []);

  const openEdit = (box = null) => {
    if (box) {
      setCurrentBox(box);
      setFormData({
        title: box.title,
        description: box.description,
        icon: box.icon || "Globe",
        show_on_homepage: box.show_on_homepage,
        show_on_services: box.show_on_services,
      });
    } else {
      setCurrentBox(null);
      setFormData({ title: "", description: "", icon: "Globe", show_on_homepage: true, show_on_services: true });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      if (currentBox) {
        await axios.put(`${API}/service-boxes/${currentBox.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Service box updated");
      } else {
        const order = boxes.length > 0 ? Math.max(...boxes.map(b => b.order)) + 1 : 1;
        await axios.post(`${API}/service-boxes`, { ...formData, order }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Service box created");
      }
      setIsEditing(false);
      fetchBoxes();
    } catch (error) {
      toast.error("Failed to save service box");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service box?")) return;
    try {
      await axios.delete(`${API}/service-boxes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Service box deleted");
      fetchBoxes();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const moveItem = async (index, direction) => {
    const sorted = [...boxes].sort((a, b) => a.order - b.order);
    const target = index + direction;
    if (target < 0 || target >= sorted.length) return;

    const updates = sorted.map((box, i) => {
      if (i === index) return { id: box.id, order: sorted[target].order };
      if (i === target) return { id: box.id, order: sorted[index].order };
      return { id: box.id, order: box.order };
    });

    try {
      await axios.put(`${API}/service-boxes-reorder`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchBoxes();
    } catch {
      toast.error("Failed to reorder");
    }
  };

  const toggleVisibility = async (box, field) => {
    try {
      await axios.put(`${API}/service-boxes/${box.id}`, { [field]: !box[field] }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchBoxes();
    } catch {
      toast.error("Failed to update");
    }
  };

  const homepageCount = boxes.filter(b => b.show_on_homepage).length;
  const servicesCount = boxes.filter(b => b.show_on_services).length;
  const sorted = [...boxes].sort((a, b) => a.order - b.order);

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="service-boxes-title">Service Boxes</h1>
          <p className="text-sm text-its-offwhite/60 mt-1">
            Homepage: {homepageCount}/5 shown &nbsp;|&nbsp; Services page: {servicesCount}/7 shown
          </p>
        </div>
        <button
          onClick={() => openEdit()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-service-box-btn"
        >
          <Plus size={18} /> Add Service Box
        </button>
      </div>

      <div className="space-y-3">
        {sorted.map((box, index) => (
          <div
            key={box.id}
            className="flex items-center gap-4 p-4 rounded-lg bg-its-teal/50 border border-its-teal-light/10"
            data-testid={`service-box-${box.id}`}
          >
            {/* Reorder arrows */}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => moveItem(index, -1)}
                disabled={index === 0}
                className="p-1 text-its-offwhite/40 hover:text-white disabled:opacity-30"
              >
                ▲
              </button>
              <button
                onClick={() => moveItem(index, 1)}
                disabled={index === sorted.length - 1}
                className="p-1 text-its-offwhite/40 hover:text-white disabled:opacity-30"
              >
                ▼
              </button>
            </div>

            <GripVertical size={18} className="text-its-offwhite/40" />

            {/* Icon + Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-its-gold text-sm">{box.icon}</span>
                <h3 className="font-medium text-white">{box.title}</h3>
              </div>
              <p className="text-sm text-its-offwhite/60 line-clamp-1 mt-1">{box.description}</p>
            </div>

            {/* Visibility toggles */}
            <button
              onClick={() => {
                if (!box.show_on_homepage && homepageCount >= 5) {
                  toast.error("Maximum 5 boxes on homepage");
                  return;
                }
                toggleVisibility(box, "show_on_homepage");
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                box.show_on_homepage
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  : "bg-its-teal border border-its-teal-light/20 text-its-offwhite/40 hover:text-its-offwhite/60"
              }`}
              title={box.show_on_homepage ? "Shown on homepage — click to hide" : "Hidden from homepage — click to show"}
            >
              <Home size={14} />
              Home
            </button>

            <button
              onClick={() => {
                if (!box.show_on_services && servicesCount >= 7) {
                  toast.error("Maximum 7 boxes on services page");
                  return;
                }
                toggleVisibility(box, "show_on_services");
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                box.show_on_services
                  ? "bg-its-gold/20 text-its-gold border border-its-gold/30"
                  : "bg-its-teal border border-its-teal-light/20 text-its-offwhite/40 hover:text-its-offwhite/60"
              }`}
              title={box.show_on_services ? "Shown on services — click to hide" : "Hidden from services — click to show"}
            >
              <Briefcase size={14} />
              Services
            </button>

            <button onClick={() => openEdit(box)} className="p-2 text-its-offwhite/60 hover:text-white transition-colors">
              <Edit size={18} />
            </button>
            <button onClick={() => handleDelete(box.id)} className="p-2 text-its-offwhite/60 hover:text-red-400 transition-colors">
              <Trash2 size={18} />
            </button>
          </div>
        ))}

        {boxes.length === 0 && (
          <div className="text-center py-12 text-its-offwhite/50">
            No service boxes yet. Click "Add Service Box" to create one.
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-its-teal-dark border-its-teal-light/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {currentBox ? "Edit Service Box" : "Add Service Box"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="form-label">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="form-input"
                placeholder="e.g. Website Development"
                data-testid="service-box-title-input"
              />
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input"
                rows={3}
                placeholder="Brief description of this service..."
                data-testid="service-box-desc-input"
              />
            </div>
            <div>
              <label className="form-label">Icon</label>
              <div className="grid grid-cols-6 gap-2">
                {ICON_OPTIONS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon })}
                    className={`p-2 rounded-lg text-xs text-center transition-colors ${
                      formData.icon === icon
                        ? "bg-its-gold/20 text-its-gold border border-its-gold/40"
                        : "bg-its-teal/50 text-its-offwhite/60 border border-its-teal-light/10 hover:text-white"
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_on_homepage}
                  onChange={(e) => setFormData({ ...formData, show_on_homepage: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-its-offwhite">Show on Homepage</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_on_services}
                  onChange={(e) => setFormData({ ...formData, show_on_services: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-its-offwhite">Show on Services</span>
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} className="btn-primary" data-testid="save-service-box-btn">
                {currentBox ? "Update" : "Create"}
              </button>
              <button onClick={() => setIsEditing(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminServiceBoxes;
